import { ActionButton, Stack } from '@fluentui/react';
import { IPSeparator, IPStack } from 'baseComponents';
import GenericModal from 'components/Dialogs/GenericModal';
import WarningMessage from 'components/Notification/WarningMessage';
import { JSONViewer } from 'components/Utils/JSONViewer';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { viewIcon } from 'globalStyles';
import { Integration } from 'models/integrations/integration';
import { IIntegrationEvidence } from 'models/integrations/integrationEvidence';
import { IntegrationTypeHandler, getIntegrationTypes } from 'models/integrations/integrationTypeHandler';
import KPIData from 'models/kpi/kpiData';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import logger from 'services/Logging/logService';
import { toLocaleDateTime } from 'utils/datetime';

interface IIntegrationEvidenceReportModalProps {
  isOpen: boolean;
  integration: Integration;
  kpiData: KPIData;
  onClose: () => void;
}

export const IntegrationEvidenceReportModal = (props: IIntegrationEvidenceReportModalProps) => {
  const { t } = useTranslation(['translation', 'integrations']);
  const [evidence, setEvidence] = useState<IIntegrationEvidence | undefined>(undefined);
  const [evidenceReport, setEvidenceReport] = useState<string>('');
  const [integrationTypeHandlers] = useState<IntegrationTypeHandler[]>(getIntegrationTypes(t));

  useEffect(() => {
    try {
      const integrationType = integrationTypeHandlers.find(
        (handler) => handler.integrationTypeId === props.integration.integrationTypeId,
      );
      if (integrationType && props.kpiData.resultEvidence) {
        const evidence = integrationType.parseEvidence(props.kpiData.resultEvidence);
        setEvidence(evidence);
        const markdown = evidence?.toMarkdownReport(t);
        setEvidenceReport(markdown || '');
      } else {
        setEvidence(undefined);
        setEvidenceReport('');
      }
    } catch (err) {
      logger.debug('Failed to parse evidence result, showing raw json. Error:', err);
      setEvidenceReport('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.kpiData]);

  //
  // Main render
  //
  return (
    <GenericModal
      isOpen={props.isOpen}
      title={props.integration.kpi.name + ' (' + toLocaleDateTime(props.kpiData.created) + ')'}
      onClose={props.onClose}
      height={700}
      width={800}
      maxWidth={'min(90vw, 800px)'}
      maxHeight={'90vh'}
      minHeight={300}
      minWidth={300}
    >
      <IPSeparator />
      <IPStack verticalFill>
        <ScrollableStackItem>
          {evidenceReport && (
            <Stack styles={{ root: { padding: 10 } }}>
              {evidence?.isCapped && <WarningMessage message={t('integrations:Evidence.IsCapped')} />}
              <Stack.Item>
                <ActionButton
                  iconProps={viewIcon}
                  text={t('integrations:Evidence.ViewAsJSON')}
                  onClick={() => setEvidenceReport('')}
                />
              </Stack.Item>
              <Stack.Item>
                <Markdown>{evidenceReport}</Markdown>
              </Stack.Item>
            </Stack>
          )}
          {!evidenceReport && props.kpiData.resultEvidence && <JSONViewer data={props.kpiData.resultEvidence} />}
          {!evidenceReport && !props.kpiData.resultEvidence && (
            <Stack styles={{ root: { padding: 10 } }}>
              <Stack.Item>{t('integrations:Evidence.NoEvidence')}</Stack.Item>
            </Stack>
          )}
        </ScrollableStackItem>
      </IPStack>
    </GenericModal>
  );
};
